import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn, truncateId } from '../../lib/utils.js';
import { useToast } from '../../context/ToastContext.js';

export interface CopyButtonProps {
  value: string;
  truncate?: boolean;
  visibleChars?: number;
  className?: string;
}

export function CopyButton({ value, truncate = true, visibleChars = 8, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const { success } = useToast();

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  const displayText = truncate ? truncateId(value, visibleChars) : value;

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Click to copy: ${value}`}
      className={cn(
        'inline-flex items-center gap-1.5 font-mono text-xs text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group',
        className
      )}
    >
      <span>{displayText}</span>
      {copied ? (
        <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 shrink-0 transition-opacity" />
      )}
    </button>
  );
}
